import {
    createContext, PropsWithChildren, useContext,
    useRef, useState, useEffect,
    useLayoutEffect
} from "react";
import { Box, Button, Modal, SxProps, Theme, Typography, InputBase, Autocomplete } from "@mui/material";
import gsap from "gsap";
import { SplitText } from "gsap/SplitText";
import { CustomEase } from "gsap/CustomEase";
import { countries } from "../enums/countries.enum";
import { galop } from "../enums/register.enums";
import { t } from "i18next";
import { useSelector } from "react-redux";
import { RootState } from "../store";
import { TUser, TuserAccountType } from "../shared/types/user.type";
import { useAuth } from "../hooks/useAuth";
import dayjs from "dayjs";
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { LoadingButton } from "@mui/lab";
import { toast } from "react-toastify";

dayjs.extend(utc)
dayjs.extend(timezone)

gsap.registerPlugin(SplitText, CustomEase);
CustomEase.create("award", "M0,0 C0.16,1 0.3,1 1,1");

interface FirstLoginContextType {
    isFirstTime: boolean;
    toggleFirstTime: (value: boolean) => void;
}

const FirstLoginContext = createContext<FirstLoginContextType | null>(null);



export const FirstLoginProvider = ({ children }: PropsWithChildren) => {
    const [firstLogin, setFirstLogin] = useState(false);


    const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
    const [accountType, setAccountType] = useState<TuserAccountType | null>(null);

    const { auth: user, updateUser, isLoading } = useAuth()


    useEffect(() => {
        if (user) {
            setSelectedCountry(user.nationality)
            setAccountType(user.type)
        }
    }, [user])

    const titleRef = useRef<HTMLElement>(null);
    const descRef = useRef<HTMLElement>(null);
    const dividerRef = useRef<HTMLDivElement>(null);
    const input1Ref = useRef<HTMLDivElement>(null);
    const input2Ref = useRef<HTMLDivElement>(null);
    const btnRef = useRef<HTMLButtonElement>(null);

    useLayoutEffect(() => {
        if (!firstLogin) return;

        const raf = requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                if (!titleRef.current || !descRef.current) return;

                const splitTitle = new SplitText(titleRef.current, {
                    type: "words",
                    wordsClass: "title-word",
                });

                const splitDesc = new SplitText(descRef.current, {
                    type: "lines",
                    linesClass: "desc-line",
                    lineThreshold: 0.1,
                });

                gsap.set(splitTitle.words, { y: 30, opacity: 0, rotateZ: 5 });
                gsap.set(splitDesc.lines, { y: 30, opacity: 0, rotateZ: 5 });
                gsap.set(dividerRef.current, { scaleX: 0, opacity: 0, transformOrigin: "left center" });
                gsap.set([input1Ref.current, input2Ref.current], { y: 24, opacity: 0 });
                gsap.set(btnRef.current, { y: 16, opacity: 0 });

                const tl = gsap.timeline({ delay: 0.1 });

                tl.to(splitTitle.words, {
                    y: 0,
                    opacity: 1,
                    rotateZ: 0,
                    duration: 1.1,
                    ease: "award",
                    stagger: 0.07,
                });

                tl.to(splitDesc.lines, {
                    y: 0,
                    opacity: 1,
                    duration: 1,
                    rotateZ: 0,
                    ease: "award",
                    stagger: 0.1,
                }, "-=0.7");

                tl.to(dividerRef.current, {
                    scaleX: 1,
                    opacity: 1,
                    duration: 0.8,
                    ease: "award",
                }, "-=0.6");

                tl.to([input1Ref.current, input2Ref.current], {
                    y: 0,
                    opacity: 1,
                    duration: 0.9,
                    ease: "award",
                    stagger: 0.08,
                }, "-=0.6");

                tl.to(btnRef.current, {
                    y: 0,
                    opacity: 1,
                    duration: 0.8,
                    ease: "award",
                }, "-=0.3");
            });
        });

        return () => cancelAnimationFrame(raf);
    }, [firstLogin]);

    const handleClose = (
        _event: React.SyntheticEvent | {},
        reason: "backdropClick" | "escapeKeyDown"
    ): void => {
        if (reason === "backdropClick") return;
        setFirstLogin(false);
    };

    const saveUserPreferences = async () => {
        if (!selectedCountry || !accountType) return
        try {

            const userTz = dayjs.tz.guess();


            await updateUser({
                id: user.id,
                nationality: selectedCountry,
                type: accountType,
                timezone: userTz,
                reset_default_settings: true

            })

            setFirstLogin(false);



        } catch (error) {
            toast(t('error:generic'), { type: 'error' })
        }
    }

    return (
        <FirstLoginContext.Provider value={{ isFirstTime: firstLogin, toggleFirstTime: setFirstLogin }}>
            {children}
            <Modal
                open={firstLogin}
                onClose={handleClose}
                slotProps={{
                    backdrop: {
                        sx: {
                            backdropFilter: "blur(12px)",
                            backgroundColor: "rgba(0,0,0,0.7)",
                        },
                    },
                }}
            >
                <Box sx={modalStyle}>
                    <Box sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "6px",
                        mb: 3,
                        px: 1.5,
                        py: 0.5,
                        border: "1px solid rgba(255,255,255,0.12)",
                        borderRadius: "100px",
                        width: "fit-content",
                        mx: "auto",
                    }}>

                        <Typography sx={{
                            fontSize: "0.7rem",
                            letterSpacing: "0.12em",
                            textTransform: "uppercase",
                            color: "rgba(255,255,255,0.5)",
                            fontWeight: 500,
                        }}>
                            {t('welcome:hello', { s: user?.full_name })}
                        </Typography>
                    </Box>

                    <Typography
                        ref={titleRef}
                        variant="h3"
                        fontWeight={600}
                        sx={{
                            textAlign: "center",
                            color: "#fff",
                            mb: 2,
                            letterSpacing: "-0.03em",
                            lineHeight: 1.1,
                            "& .title-word": {
                                display: "inline-block",
                                overflow: "hidden",
                                transformOrigin: "bottom center",
                                verticalAlign: "bottom",
                                marginRight: "0.08em",
                            },
                        }}
                    >
                        {t('modal:welcomeToEqm')}
                    </Typography>

                    <Typography
                        ref={descRef}
                        sx={{
                            mb: 3,
                            textAlign: "center",
                            fontSize: "0.9rem",
                            color: "rgba(255,255,255,0.4)",
                            lineHeight: 1.7,
                            "& .desc-line": {
                                display: "block",
                                overflow: "hidden",
                            },
                        }}
                    >
                        {t('modal:welcomeToEqmDesc')}
                    </Typography>

                    <Box
                        ref={dividerRef}
                        sx={{
                            height: "1px",
                            bgcolor: "rgba(255,255,255,0.08)",
                            mb: 3,
                            mx: -3,
                        }}
                    />

                    {/* Country */}
                    <Box ref={input1Ref} sx={inputWrapperStyle}>
                        <Typography sx={inputLabelStyle}>{t('info:placeholderCountry')}</Typography>
                        <Autocomplete
                            options={countries}
                            getOptionLabel={(option) => option.name}
                            getOptionKey={(option) => option.code}
                            value={countries.find(country => country.code === selectedCountry)}
                            isOptionEqualToValue={(option, value) => option.code === value.code}
                            onChange={(_, value) => setSelectedCountry(value?.code ?? null)}
                            fullWidth
                            renderInput={(params) => (
                                <InputBase
                                    {...params.InputProps}
                                    inputProps={params.inputProps}
                                    fullWidth
                                    sx={inputBaseStyle}
                                />
                            )}
                        />
                    </Box>

                    {/* Account Type */}
                    <Box ref={input2Ref} sx={{ ...inputWrapperStyle, mb: 3 }}>
                        <Typography sx={inputLabelStyle}>{t('info:accountType')}</Typography>
                        <Autocomplete
                            options={galop}
                            getOptionLabel={(option) => t(option.key)}
                            getOptionKey={option => option.value}
                            value={galop.find(g => g.value === accountType)}
                            isOptionEqualToValue={(option, value) => option.value === value.value}

                            onChange={(_, value) => setAccountType((value?.value as TuserAccountType) ?? null)}
                            fullWidth
                            renderInput={(params) => (
                                <InputBase
                                    {...params.InputProps}
                                    inputProps={params.inputProps}
                                    fullWidth
                                    sx={inputBaseStyle}
                                />
                            )}
                        />
                    </Box>

                    <LoadingButton
                        loading={isLoading}
                        ref={btnRef}
                        onClick={saveUserPreferences}
                        variant="contained"
                        fullWidth
                        disabled={!selectedCountry || !accountType}
                        disableElevation
                        sx={{
                            fontWeight: 600,
                            fontSize: "0.85rem",
                            letterSpacing: "0.04em",
                            textTransform: "none",
                            py: 1.5,
                            borderRadius: "6px",

                        }}
                    >
                        {t('    continues')} →
                    </LoadingButton>
                </Box>
            </Modal>
        </FirstLoginContext.Provider>
    );
};

const modalStyle: SxProps<Theme> = {
    position: "absolute" as const,
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 460,
    bgcolor: "#111",
    borderRadius: "12px",
    border: "1px solid rgba(255,255,255,0.07)",
    boxShadow: "0 32px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.04)",
    p: 4,
    outline: "none",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
};

const inputWrapperStyle: SxProps<Theme> = {
    width: "100%",
    mb: 2,
    p: "12px 16px",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: "8px",
    bgcolor: "rgba(255,255,255,0.03)",
    transition: "border-color 0.2s ease",
    "&:focus-within": {
        borderColor: "rgba(255,255,255,0.2)",
        bgcolor: "rgba(255,255,255,0.05)",
    },
};

const inputLabelStyle: SxProps<Theme> = {
    fontSize: "0.68rem",
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    color: "rgba(255,255,255,0.3)",
    fontWeight: 500,
    mb: 0.5,
};

const inputBaseStyle: SxProps<Theme> = {
    color: "#fff",
    fontSize: "0.95rem",
    "& input::placeholder": {
        color: "rgba(255,255,255,0.2)",
        opacity: 1,
    },
};

export const useFirstLogin = () => {
    const context = useContext(FirstLoginContext);
    if (!context) throw new Error("useFirstLogin must be used within a FirstLoginProvider");
    return context;
};